import { useCallback, useEffect, useMemo } from 'react';

import { useParams } from 'react-router-dom';
import lodash from 'lodash';

import { useAssessmentActionsContext } from '~/lib/contexts/AssessmentActionsContext';
import { useGetPatientQuery } from '~/store/services/patients';
import { useApi } from '~/lib/hooks';
import { ApiCommand } from '~/lib/Api';
import urlConstants from '~/lib/constants/url.constants';
import { OrganizeBy } from '~/lib/constants/task.constants';
import {
  CompServTask,
  FetchTasksResponse,
  getCombinedSortedTasksForPatient,
  TaskStatusLabel,
} from '~/lib/util/task.util';
import { getPreferredFirstName } from '~/lib/util/patient.util';
import PatientOverviewTabSwitcher from '~/components/maud/patients/profile/tabs/PatientOverviewTabSwitcher';

const ALL_TASKS_PAGE_SIZE = 3;
const { listTasks: listTasksUrl } = urlConstants.tasks;

const OverviewPage = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const { data: patient } = useGetPatientQuery(
    { patientId },
    { skip: !patientId },
  );

  const assessmentActions = useAssessmentActionsContext();

  const {
    data: openMaudTasksData,
    error: openMaudTasksError,
    sendRequest: getOpenMaudTasks,
  } = useApi<FetchTasksResponse>();
  const {
    data: openFamilyTasksData,
    error: openFamilyTasksError,
    sendRequest: getOpenFamilyTasks,
  } = useApi<FetchTasksResponse>();

  const fetchTasks = useCallback((): void => {
    if (!patientId) return;
    const maudTaskOptions = {
      'filter-by': {
        'is-family-task': false,
        'patient-external-id': patientId,
        status: [TaskStatusLabel.TO_DO, TaskStatusLabel.IN_PROGRESS],
      },
      'order-by': OrganizeBy.DUE_DATE,
      'page-number': 1,
      'page-size': ALL_TASKS_PAGE_SIZE,
    };
    const familyTaskOptions = lodash.cloneDeep(maudTaskOptions);
    familyTaskOptions['filter-by']['is-family-task'] = true;
    familyTaskOptions['order-by'] = OrganizeBy.CREATED_DATE;

    const baseParams = {
      command: ApiCommand.GET,
      url: listTasksUrl,
    };
    getOpenMaudTasks({ ...baseParams, options: maudTaskOptions });
    getOpenFamilyTasks({ ...baseParams, options: familyTaskOptions });
  }, [patientId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const openTasks = useMemo<CompServTask[]>(
    () =>
      getCombinedSortedTasksForPatient(openMaudTasksData, openFamilyTasksData),
    [openMaudTasksData, openFamilyTasksData],
  );

  if (!patientId || !patient) return null;

  const patientFirstName = getPreferredFirstName(
    patient.chosenName,
    patient.firstName,
  );

  return (
    <PatientOverviewTabSwitcher
      assessmentList={assessmentActions.assessmentList}
      familyTaskCount={openFamilyTasksData?.totalCount}
      isAssessmentError={assessmentActions.isAssessmentError}
      isAssessmentLoading={assessmentActions.isAssessmentLoading}
      isTasksError={!!openMaudTasksError || !!openFamilyTasksError}
      maudTaskCount={openMaudTasksData?.totalCount}
      onSendEmail={assessmentActions.onSendEmail}
      onUpdateAssessmentStatus={assessmentActions.onUpdateAssessmentStatus}
      openTasks={openTasks}
      patient={patient}
      patientFirstName={patientFirstName}
      patientId={patientId}
      patientLastName={patient.lastName}
      selectedSurveyResults={assessmentActions.selectedSurveyResults}
      surveys={assessmentActions.surveys}
      updateAssessmentState={assessmentActions.updateAssessmentState}
    />
  );
};

export default OverviewPage;
